import { z } from "zod";

/**
 * Phase 7 outreach & nurture — the playbook's Part 4/5 as data. Seeded
 * planner/venue targets came from an LLM playbook and are `verified: false`
 * until Chris confirms each one by hand; the UI leans hard on that signal
 * (see OutreachDetailDialog / the "unverified only" filter on the board
 * page) rather than treating it as a minor field.
 *
 * `contact_name` arrives FLAT on the outreach row (not nested under a
 * `contact` object) — matches the contract exactly and mirrors how
 * inquiry.ts's BoardItem resolves `contact_name` flat rather than joining
 * client-side.
 */

export const OutreachTierSchema = z.enum([
  "warm",
  "planner",
  "venue",
  "corporate",
  "milestone",
]);
export type OutreachTier = z.infer<typeof OutreachTierSchema>;

export const TIERS: OutreachTier[] = ["warm", "planner", "venue", "corporate", "milestone"];

export const TIER_LABELS: Record<OutreachTier, string> = {
  warm: "Warm",
  planner: "Planner",
  venue: "Venue",
  corporate: "Corporate",
  milestone: "Milestone",
};

export const TIER_BADGE_VARIANT: Record<
  OutreachTier,
  "default" | "success" | "warning" | "danger" | "info" | "secondary" | "primary"
> = {
  warm: "secondary",
  planner: "primary",
  venue: "info",
  corporate: "warning",
  milestone: "success",
};

export const OutreachStatusSchema = z.enum([
  "identified",
  "contacted",
  "tasting_offered",
  "tasting_done",
  "on_vendor_list",
  "active_referrer",
  "dead",
]);
export type OutreachStatus = z.infer<typeof OutreachStatusSchema>;

// Kanban column order — the playbook's tiered-outreach funnel.
export const STATUSES: OutreachStatus[] = [
  "identified",
  "contacted",
  "tasting_offered",
  "tasting_done",
  "on_vendor_list",
  "active_referrer",
  "dead",
];

export const STATUS_LABELS: Record<OutreachStatus, string> = {
  identified: "Identified",
  contacted: "Contacted",
  tasting_offered: "Tasting offered",
  tasting_done: "Tasting done",
  on_vendor_list: "On vendor list",
  active_referrer: "Active referrer",
  dead: "Dead",
};

// vendor_list_status is a free-form field per the API contract (no enum is
// specified there); the plan's prose names a applied -> tasting -> listed
// pipeline, so the UI offers those as quick picks while the schema stays
// permissive (accepts any string, including values seeded outside this list).
export const VENDOR_LIST_STATUS_OPTIONS = ["applied", "tasting", "listed"] as const;

export const OutreachSchema = z.object({
  id: z.string(),
  contact_id: z.string().nullable(),
  contact_name: z.string().nullable(),
  tier: OutreachTierSchema,
  status: OutreachStatusSchema,
  last_touch_at: z.string().nullable(),
  next_touch_at: z.string().nullable(),
  vendor_list_status: z.string().nullable(),
  verified: z.boolean(),
  notes: z.string().nullable(),
  // Present on the list/detail row; the board projection carries its own
  // (lighter) copy below rather than reusing this schema outright.
  days_since_touch: z.number().nullable().optional(),
  created_at: z.string(),
});
export type Outreach = z.infer<typeof OutreachSchema>;

export const OutreachListResponseSchema = z.object({
  items: z.array(OutreachSchema),
  next_cursor: z.string().nullable(),
});
export type OutreachListResponse = z.infer<typeof OutreachListResponseSchema>;

// ---- Board -----------------------------------------------------------

// GET /api/v2/outreach/board — a lighter-weight kanban projection, keyed by
// `status` (not `stage` — the outreach funnel's column key per the contract).
export const OutreachBoardItemSchema = z.object({
  id: z.string(),
  contact_name: z.string().nullable(),
  tier: OutreachTierSchema,
  status: OutreachStatusSchema,
  verified: z.boolean(),
  next_touch_at: z.string().nullable(),
  days_since_touch: z.number().nullable(),
});
export type OutreachBoardItem = z.infer<typeof OutreachBoardItemSchema>;

export const OutreachBoardColumnSchema = z.object({
  status: OutreachStatusSchema,
  items: z.array(OutreachBoardItemSchema),
});
export type OutreachBoardColumn = z.infer<typeof OutreachBoardColumnSchema>;

export const OutreachBoardResponseSchema = z.object({
  columns: z.array(OutreachBoardColumnSchema),
});
export type OutreachBoardResponse = z.infer<typeof OutreachBoardResponseSchema>;

// ---- Weekly rhythm -----------------------------------------------------

// GET /api/v2/outreach/rhythm — the weekly quota board (planner touches,
// venue applications, corporate emails, review-ask streak). done/target are
// server-authoritative counts derived from activity rows; the client only
// ever renders them, never recomputes.
export const RhythmQuotaSchema = z.object({
  key: z.string(),
  label: z.string(),
  target: z.number(),
  done: z.number(),
});
export type RhythmQuota = z.infer<typeof RhythmQuotaSchema>;

export const RhythmResponseSchema = z.object({
  week_start: z.string(),
  quotas: z.array(RhythmQuotaSchema),
});
export type RhythmResponse = z.infer<typeof RhythmResponseSchema>;

// ---- Forms -----------------------------------------------------------

// "New outreach target" intake. The contract's create body is
// `{contact_id?|name+tier fields, tier}` — this build resolves that as
// {name, tier}, creating the contact inline the same way the pipeline's
// "New inquiry" dialog takes a bare contact_name rather than a contact
// picker (no contact-search/combobox component exists yet in the UI kit).
export const OutreachFormSchema = z.object({
  name: z.string().min(1, "Contact name is required"),
  tier: OutreachTierSchema,
});
export type OutreachFormValues = z.infer<typeof OutreachFormSchema>;

export const EMPTY_OUTREACH_FORM_VALUES: OutreachFormValues = {
  name: "",
  tier: "planner",
};
